<script lang="ts" setup>
	import CountrySelect from '@/components/CountrySelect.vue';
	import VarietyExpandFilterComponent from '@/components/filters/VarietyExpandFilterComponent.vue';
	import VarietyInlineFilterComponent from '@/components/filters/VarietyInlineFilterComponent.vue';
	import VarietySurplusFilterComponent from '@/components/filters/VarietySurplusFilterComponent.vue';
	import VarietyUnavailableProductsComponent
		from '@/components/filters/VarietyUnavailableProductsFilterComponent.vue';
	import {useFilters} from '@/store/filter.store';
	import type {Filter} from '@/types/search/filter';
	import type {Option} from '@/types/search/filter.option';
	import {storeToRefs} from 'pinia';
	import {computed} from 'vue';
	import {useAppStore} from '@/store/app.store';
	import {Route} from '@/router/route';
	import {useRoute} from 'vue-router';

	const emit = defineEmits(['filters-updated']);

	defineProps<{
		numberOfResults: number;
	}>();

	const appStore = useAppStore();
	const filterStore = useFilters();
	const route = useRoute();
	const {
		filters, filtersMinor, selectedFilters, surplusFilter,
		tooltips, hideUnavailableFilter, selectedProducts
	} = storeToRefs(filterStore);

	const filtersUpdated = () => {
		emit('filters-updated');
	};

	const filtersActive = computed(() => {
		return selectedFilters.value?.length > 0
			|| surplusFilter.value
			|| hideUnavailableFilter.value === false
			|| selectedProducts.value?.length > 0;
	});

	const getActiveOptions = (filter: Filter) => {
		return filter.options.filter((option: Option) => option.active);
	};

	const isAvailabilityView = computed(() => route.name === Route.AVAILABILITY_OVERVIEW);

	const activeFilterOptionsAmount = computed(() => {
		return filters.value.reduce((sum, filter) => {
			return sum + getActiveOptions(filter).length;
		}, 0);
	});

	const resetAllFilters = () => {
		filterStore.resetFilters();
		filtersUpdated();
	};

	const toggleFilters = (): void => {
		filterStore.collapseActiveFilter();
		appStore.toggleFilterBlock();
	};

	const expandedFilter = () => filterStore.expandedFilter();

	const getTooltipForExpandedFilter = () => {
		if (expandedFilter === null || tooltips.value === null) {
			return undefined;
		}

		const value = tooltips.value[expandedFilter.name];

		if (!value) {
			return undefined;
		}

		return value;
	};

</script>

<template>
	<div class="catalog-filters">
		<div class="catalog-filters__header">
			<h2>
				<button type="button" @click="toggleFilters">
					<span class="icon">
						<svg class="icon-arrow-right" height="17" width="19">
							<use xlink:href="/assets/images/build/svg-sprite.catalog.min.svg#arrow-right"></use>
						</svg>
					</span>
				</button>

				<span class="text" @click="toggleFilters">
					{{ $t('site.products.overview.filters.title') }}
				</span>
			</h2>
		</div>

		<div class="filter-overlay-background" @click="toggleFilters" />

		<div class="filter-overlay-container">
			<div class="filter-overlay__header">
				<div class=" filter-overlay__header--main">
					<button @click.prevent="toggleFilters">
						<svg class="icon-modal-close" height="20" width="20">
							<use xlink:href="/assets/images/build/svg-sprite.min.svg#modal-close" />
						</svg>
					</button>
					{{ $t('site.products.overview.filters.title') }}
				</div>
			</div>

			<!-- Mobile reset filters block -->
			<div class="filter-overlay__content">
				<div class="filter-overlay__currently-active">
					<a v-if="filtersActive"
					   class="reset-filters"
					   href="#"
					   @click.stop.prevent="resetAllFilters">
						{{ $t('site.products.overview.filters.reset.button.mobile.text') }}
					</a>
					<span class="number-of-active-filters">{{ activeFilterOptionsAmount }}</span>
					<span>Filters</span>
				</div>

				<div class="filter-list">
					<CountrySelect @filters-updated="filtersUpdated" />

					<!-- Desktop reset filters block -->
					<div v-if="filtersActive"
					     id="filter-part--reset-button"
					     class="filter-part filter-part--reset-button">

						<button class="button button--small"
						        @click.stop.prevent="resetAllFilters">
							{{ $t('site.products.overview.filters.reset.button.desktop.text') }}
						</button>
					</div>

					<div v-for="filter in filters" :key="filter.name">
						<VarietyExpandFilterComponent v-if="filter.configuration.type === 'EXPAND'"
						                              :filter="filter"
						                              :tooltip="tooltips != null ? tooltips[filter.name] : undefined"
						                              @filters-updated="filtersUpdated" />

						<VarietyInlineFilterComponent v-if="filter.configuration.type === 'INLINE'"
						                              :filter="filter"
						                              :tooltip="tooltips != null ? tooltips[filter.name] : undefined"
						                              @filters-updated="filtersUpdated" />
					</div>

					<div v-if="isAvailabilityView" id="filter-custom" class="filter-custom">
						<div>
							<VarietyUnavailableProductsComponent @unavailable-updated="filtersUpdated" />
						</div>

						<div class="surplus-filter-block filter-margin-bottom">
							<VarietySurplusFilterComponent @surplus-updated="filtersUpdated" />
						</div>
					</div>

					<div class="filter-margin-top">
						<div v-for="filter in filtersMinor" :key="filter.name">
							<VarietyExpandFilterComponent v-if="filter.configuration.type === 'EXPAND'"
							                              :filter="filter"
							                              :tooltip="tooltips != null ? tooltips[filter.name] : undefined"
							                              @filters-updated="filtersUpdated" />

							<VarietyInlineFilterComponent v-if="filter.configuration.type === 'INLINE'"
							                              :filter="filter"
							                              :tooltip="tooltips != null ? tooltips[filter.name] : undefined"
							                              @filters-updated="filtersUpdated" />
						</div>
					</div>

					<div class="filter-active-overlay" @click="filterStore.collapseActiveFilter"></div>

				</div>
			</div>

			<div class="filter-overlay__footer">
				<div v-if="numberOfResults" class="catalog-overview-header__title">
					<span>{{ $t('site.products.overview.header.results.partbeforeamount.title') }}</span>
					<strong v-text="numberOfResults" />
					{{ $t('site.products.overview.header.results.partafteramount.title') }}
				</div>

				<button v-if="numberOfResults" class="button button--green" @click.prevent="toggleFilters">
					{{ $t('site.products.overview.header.results.mobile.partbeforeamount.title') }}
					<span v-text="numberOfResults" />
					{{ $t('site.products.overview.header.results.mobile.partafteramount.title') }}

					<span class="filter-spinner"></span>
				</button>
			</div>
		</div>

		<div class="filter-overlay-container filter-overlay-container--sub">
			<div class="filter-overlay__header filter-overlay__header--sub" @click="filterStore.collapseActiveFilter">
				<svg class="icon-chevron-left" height="12" width="9">
					<use xlink:href="/assets/images/build/svg-sprite.catalog.min.svg#chevron-left" />
				</svg>
				{{ $t('site.products.overview.backtofilters.text') }}'
			</div>

			<div class="filter-overlay__content filter-overlay__content--sub">
				<VarietyExpandFilterComponent v-if="expandedFilter()"
				                              :filter="expandedFilter()"
				                              :tooltip="getTooltipForExpandedFilter()"
				                              @filters-updated="filtersUpdated" />
			</div>

			<div class="filter-overlay__footer filter-overlay__footer--sub">
				<button v-if="numberOfResults" class="button button--green" @click.prevent="toggleFilters">
					{{ $t('site.products.overview.header.results.mobile.partbeforeamount.title') }}
					<span v-text="numberOfResults" />
					{{ $t('site.products.overview.header.results.mobile.partafteramount.title') }}

					<span class="filter-spinner"></span>
				</button>
			</div>
		</div>
	</div>
</template>
